import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMG } from "../../utils/images";

const slides = [
  { img: IMG.onbGym, title: "Your Fitness, Reimagined", sub: "AI-powered workouts built around you." },
  { img: IMG.onbFood, title: "Eat Smart, Not Less", sub: "Scan meals, track macros, eat right." },
  { img: IMG.onbCommunity, title: "Your City, Your Community", sub: "Join thousands training across India." },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (i === slides.length - 1) return;
    const t = setTimeout(() => setI((x) => x + 1), 3500);
    return () => clearTimeout(t);
  }, [i]);

  const finish = () => navigate("/profile-setup", { replace: true });

  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid="onboarding">
      <AnimatePresence>
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img src={slides[i].img} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.92) 100%)" }} />
          <div className="absolute bottom-32 left-6 right-6 text-white max-w-2xl mx-auto">
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="font-display text-[40px] sm:text-[44px] leading-[1.05]"
            >
              {slides[i].title}
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-3 text-[16px] text-white/80"
            >
              {slides[i].sub}
            </motion.p>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* skip */}
      <button
        type="button"
        onClick={finish}
        data-testid="onboarding-skip"
        className="absolute top-5 right-5 text-white/85 text-[13px]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        Skip
      </button>

      {/* dots */}
      <div className="absolute bottom-24 left-0 right-0 flex justify-center gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: idx === i ? 24 : 7,
              background: idx === i ? "#fff" : "rgba(255,255,255,0.5)",
            }}
          />
        ))}
      </div>

      {/* CTA on last */}
      {i === slides.length - 1 && (
        <div
          className="absolute bottom-0 left-0 right-0 px-6"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 18px)" }}
        >
          <motion.button
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            onClick={finish}
            data-testid="onboarding-get-started"
            className="w-full max-w-md mx-auto flex items-center justify-center gap-2"
            style={{
              background: "var(--accent-blue)",
              color: "#000",
              height: 52,
              borderRadius: 100,
              fontWeight: 600,
              boxShadow: "var(--shadow-float)",
            }}
          >
            Get Started <ArrowRight size={18} />
          </motion.button>
        </div>
      )}
    </div>
  );
}
